import React from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import MenuItem from '@material-ui/core/MenuItem'
import Chip from '@material-ui/core/Chip'
import ActionsMenu from 'components/actions-menu'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import PersonOutline from 'components/material-symbol/icons/PersonOutline'
import AssignmentIndOutlinedIcon from 'components/material-symbol/icons/AssignmentInd'
import EditOutlined from 'components/material-symbol/icons/Edit'
import { useIsTablet } from 'lib/hooks'
import { getInitials } from 'lib/text-display'
import hasNonNullProperty from 'lib/utils/has-non-null-property'

const PROFILE_CHIP_STYLES = {
  complete: {
    backgroundColor: '#B9F6CA',
    color: '#003D31',
    label: 'Completed Profile',
  },
  incomplete: {
    backgroundColor: '#E8E8E8',
    color: '#111111',
    label: 'Incomplete Profile',
  },
}

const useStyles = makeStyles(theme => ({
  row: {
    cursor: 'pointer',
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  studentAvatar: {
    height: '36px',
    width: '36px',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    color: '#6B7C87',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  ageGroupChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
  ageGroupChipJunior: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  ageGroupChipAdult: {
    backgroundColor: '#E0EAFF',
    color: '#001C57',
  },
  profileChip: {
    borderRadius: 8,
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    '& .MuiChip-label': {
      color: 'inherit',
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  icon: {
    color: theme.palette.grey[600],
    fontSize: '1.25rem',
  },
}))

const getRelationshipSubtitle = (student, relationshipType) => {
  if (relationshipType === 'SELF') {
    return 'Account owner'
  }

  return student?.isMinor ? 'Junior' : 'Adult'
}

const AccountStudentRow = ({ student, relationshipType }) => {
  const history = useHistory()
  const classes = useStyles()
  const isTablet = useIsTablet()
  const hasCompletedProfile = hasNonNullProperty(student?.golferProfile)
  const profileChip = hasCompletedProfile
    ? PROFILE_CHIP_STYLES.complete
    : PROFILE_CHIP_STYLES.incomplete
  const fullName = `${student.firstName} ${student.lastName}`
  const ageGroupLabel = student.isMinor ? 'Junior' : 'Adult'
  const email = student?.contact?.email

  const handleViewProfileClick = () => {
    history.push(`/account/students/${student.id}/profile/edit`)
  }

  const handleEditDetailsClick = () => {
    history.push(`/account/students/${student.id}/edit`)
  }

  return (
    <TableRow
      className={classes.row}
      data-testid={`account-student-row-${student.id}`}
      onClick={() => history.push(`/account/students/${student.id}`)}
    >
      <TableCell className={classes.info}>
        <Box className={classes.nameCell}>
          <Avatar className={classes.studentAvatar} alt={fullName}>
            {getInitials(fullName)}
          </Avatar>
          <Box className={classes.info}>
            <Typography className={classes.text} noWrap>
              {fullName}
            </Typography>
            <Typography className={classes.textSecondary} noWrap>
              {getRelationshipSubtitle(student, relationshipType)}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      {!isTablet && (
        <TableCell>
          <span
            className={`${classes.ageGroupChip} ${
              student.isMinor
                ? classes.ageGroupChipJunior
                : classes.ageGroupChipAdult
            }`}
          >
            {ageGroupLabel}
          </span>
        </TableCell>
      )}
      {!isTablet && (
        <TableCell className={classes.info}>
          <Typography className={classes.text} noWrap>
            {email}
          </Typography>
        </TableCell>
      )}
      <TableCell>
        <Chip
          label={profileChip.label}
          size="small"
          className={classes.profileChip}
          style={{
            backgroundColor: profileChip.backgroundColor,
            color: profileChip.color,
          }}
        />
      </TableCell>
      <TableCell
        className={classes.actions}
        onClick={event => event.stopPropagation()}
      >
        <ActionsMenu
          ariaId={`account-student-actions-${student.id}`}
          aria-label="Account actions"
          data-testid="menu-button"
          buttonContent={<MoreHoriz fontSize="small" />}
          className={classes.plainActionButton}
          color="default"
        >
          <MenuItem
            onClick={() => history.push(`/account/students/${student.id}`)}
          >
            <ListItemIcon className={classes.menuIcon}>
              <PersonOutline className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="View Account Details" />
          </MenuItem>
          <MenuItem onClick={handleEditDetailsClick}>
            <ListItemIcon className={classes.menuIcon}>
              <EditOutlined className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="Edit Account Details" />
          </MenuItem>
          <MenuItem
            onClick={handleViewProfileClick}
            data-testid="profile-button"
          >
            <ListItemIcon className={classes.menuIcon}>
              <AssignmentIndOutlinedIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText
              primary={
                hasCompletedProfile
                  ? 'Edit Golfer Profile'
                  : 'Complete Golfer Profile'
              }
            />
          </MenuItem>
        </ActionsMenu>
      </TableCell>
    </TableRow>
  )
}

export default AccountStudentRow
